// The Live Coach's realtime connection to the Interview Intelligence service.
//
// Browsers can't set headers on a WebSocket handshake, so the JWT rides as a
// `?token=` query param (the interview-intelligence ws endpoint reads it there). Modelled on
// `useAgentForgeWS` — same platform conventions for resilience:
//
//   1. Exponential backoff (1s → 30s) for transient server/network failures.
//   2. JWT refresh on close-before-open: a reconnect that closes without ever
//      firing `onopen` is almost always a stale `apiv1Auth`; refresh once
//      before retrying so we don't loop on a dead token.
//   3. Stale-frame watchdog: the protocol-level ws ping/pong is invisible to
//      browser JS, so a half-dead socket (LB kill, NAT timeout) would never
//      trip `onclose`. If no frame arrives for the threshold window we
//      force-close and reconnect. The interview-intelligence stream is chatty during a live round
//      (transcript + screen.state), so a long silence means the socket is dead.
//   4. Foreground reconnect: returning to the tab on a dead socket reconnects
//      immediately rather than waiting out the backoff.
//
// Subscribers receive raw `{kind, ...}` frames; the caller routes on `kind`.

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchRefreshToken } from '@/lib/auth-interceptor';
import type { CoachWSEvent } from '@/components/shared/interviews-revamp/livecoach/api';

type MessageHandler = (_event: CoachWSEvent) => void;

const MAX_RECONNECT_DELAY_MS = 30_000;
const INITIAL_RECONNECT_DELAY_MS = 1_000;
// After this long of continuous, unsuccessful reconnecting we surface a TERMINAL "disconnected" state so the
// interviewer sees a clear error instead of a badge that says "Reconnecting…" forever. We keep retrying under
// the hood (a server may still come back), but the UI stops pretending it's fine.
const RECONNECT_GIVE_UP_MS = 120_000;
// No frame (of any kind) for this long ⇒ the socket is dead, force-reconnect.
// The server sends a JS-visible `{kind:"ping"}` heartbeat every ~54s (transcripts are no longer streamed),
// so this threshold sits comfortably above that — a genuinely dead socket misses the heartbeat and trips it.
const STALE_FRAME_THRESHOLD_MS = 90_000;
const STALE_CHECK_INTERVAL_MS = 5_000;

const buildWsUrl = (sessionId: string | number, token: string): string => {
  const iiUrl = import.meta.env.VITE_INTERVIEW_INTELLIGENCE_URL;
  const host = iiUrl ? new URL(iiUrl).host : window.location.host;
  const proto = iiUrl
    ? iiUrl.startsWith('https')
      ? 'wss'
      : 'ws'
    : window.location.protocol === 'https:'
      ? 'wss'
      : 'ws';
  return `${proto}://${host}/api/v1/sessions/${sessionId}/stream?token=${token}`;
};

export interface UseInterviewIntelligenceWSResult {
  connected: boolean;
  reconnecting: boolean;
  /** Terminal: we've been reconnecting unsuccessfully past RECONNECT_GIVE_UP_MS. The UI should show a clear
   * "disconnected" error. Cleared the moment a reconnect succeeds. */
  failed: boolean;
  /** Bumped every time the socket completes a reconnect (an `onopen` after a
   * prior `onopen` in this hook's lifetime). Consumers watch it to backfill the
   * trail over HTTP — "we may have lost frames during the gap, sync up." Stays
   * at 0 for the very first connect. */
  reconnectGeneration: number;
  subscribe: (_handler: MessageHandler) => () => void;
}

export const useInterviewIntelligenceWS = (
  sessionId: string | number | undefined,
  enabled = true
): UseInterviewIntelligenceWSResult => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const handlersRef = useRef<Set<MessageHandler>>(new Set());
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  const mountedRef = useRef(true);
  const wasConnectedRef = useRef(false);
  const connectRef = useRef<() => void>();
  const lastFrameAtRef = useRef<number>(0);
  // Did the current attempt ever see `onopen`? Reset on every `connect()`. A
  // close without an open points at a stale token → refresh before retrying.
  const sawOpenThisAttemptRef = useRef(false);
  // Has a token refresh already been tried for the current bad-token cluster?
  // Reset on a successful open so a later expiry can refresh again.
  const refreshTriedRef = useRef(false);

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reconnectGeneration, setReconnectGeneration] = useState(0);
  // When the current reconnecting streak started (0 = not reconnecting). Drives the terminal `failed` flip.
  const reconnectingSinceRef = useRef(0);

  const subscribe = useCallback((handler: MessageHandler) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    const delay = reconnectDelayRef.current;
    reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
    reconnectTimeoutRef.current = setTimeout(
      () => connectRef.current?.(),
      delay
    );
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current || !enabled || !sessionId) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const token = localStorage.getItem('apiv1Auth');
    if (!token) {
      console.warn(
        '[live-coach] no `apiv1Auth` token in localStorage at this origin — WS will not connect.'
      );
      return;
    }

    sawOpenThisAttemptRef.current = false;
    const url = buildWsUrl(sessionId, token);
    const ws = new WebSocket(url);

    ws.onopen = () => {
      sawOpenThisAttemptRef.current = true;
      refreshTriedRef.current = false;
      lastFrameAtRef.current = Date.now();
      const isReconnect = wasConnectedRef.current;
      wasConnectedRef.current = true;
      reconnectingSinceRef.current = 0;
      setConnected(true);
      setReconnecting(false);
      setFailed(false);
      reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
      if (isReconnect) setReconnectGeneration((g) => g + 1);
    };

    ws.onclose = () => {
      setConnected(false);
      if (wasConnectedRef.current) {
        setReconnecting(true);
        if (reconnectingSinceRef.current === 0) {
          reconnectingSinceRef.current = Date.now();
        }
      }
      wsRef.current = null;
      if (!mountedRef.current) return;

      // Close-without-open is the canonical "token rejected" pattern. Refresh
      // once before falling into backoff; the next attempt picks up the fresh
      // token from localStorage.
      if (!sawOpenThisAttemptRef.current && !refreshTriedRef.current) {
        refreshTriedRef.current = true;
        fetchRefreshToken().finally(() => {
          reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
          reconnectTimeoutRef.current = setTimeout(
            () => connectRef.current?.(),
            0
          );
        });
        return;
      }

      scheduleReconnect();
    };

    ws.onerror = () => {
      ws.close();
    };

    ws.onmessage = (event) => {
      lastFrameAtRef.current = Date.now();
      try {
        const data = JSON.parse(event.data) as CoachWSEvent;
        if (!data?.kind) return;
        handlersRef.current.forEach((handler) => handler(data));
      } catch {
        // Ignore malformed frames.
      }
    };

    wsRef.current = ws;
  }, [enabled, sessionId, scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  // Foreground reconnect — returning to the tab on a dead socket shouldn't wait
  // out the backoff.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [connect]);

  // Give-up watchdog: flip to the terminal `failed` state once we've been reconnecting unsuccessfully past
  // the threshold, so the badge shows a clear error instead of "Reconnecting…" indefinitely.
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        reconnectingSinceRef.current > 0 &&
        Date.now() - reconnectingSinceRef.current > RECONNECT_GIVE_UP_MS
      ) {
        setFailed(true);
      }
    }, STALE_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // Stale-frame watchdog — see header note (3). A half-dead socket never trips
  // `onclose`, so we force it when frames stop arriving.
  useEffect(() => {
    const interval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const age = Date.now() - lastFrameAtRef.current;
      if (age > STALE_FRAME_THRESHOLD_MS) {
        try {
          wsRef.current.close();
        } catch {
          // already closed
        }
      }
    }, STALE_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return { connected, reconnecting, failed, reconnectGeneration, subscribe };
};
